export type LeadSource = 'PROPERTY24' | 'PRIVATE_PROPERTY' | 'KWHOMES';

export type SourceStatus = 'CONNECTED' | 'UNAVAILABLE' | 'NOT_CONNECTED' | 'CHECKING';

export function isLeadDataReady(authLoading: boolean, token: string | null, activeContextId: string | null): boolean {
  return !authLoading && Boolean(token) && Boolean(activeContextId);
}

export type PortalLeadRow = {
  id: string;
  lead_id: string;
  lead_type: string | null;
  lead_name: string | null;
  lead_email: string | null;
  lead_phone_number: string | null;
  lead_message: string | null;
  listing_reference: string | null;
  listing_external_reference: string | null;
  listing_type: string | null;
  listing_url: string | null;
  listing_address: string | null;
  listing_suburb: string | null;
  agent_id: string | null;
  agent_name: string | null;
  agent_email: string | null;
  market_center_name: string | null;
  agency_name: string | null;
  price: string | null;
  price_type: string | null;
  lead_date_time: string | null;
  lead_sent_date_time: string | null;
  received_at: string;
};

export type UnifiedLead = {
  id: string;
  source: LeadSource;
  sourceLabel: string;
  sourceLeadId: string;
  leadType: string | null;
  occurredAt: string | null;
  receivedAt: string;
  consumerName: string | null;
  phone: string | null;
  email: string | null;
  message: string | null;
  listingReference: string | null;
  listingInternalId: string | null;
  listingAgentKwuid: string | null;
  listingExternalReference: string | null;
  listingAddress: string | null;
  suburb: string | null;
  listingType: string | null;
  listingUrl: string | null;
  price: string | null;
  priceType: string | null;
  agentId: string | null;
  agentKwuid: string | null;
  agentName: string | null;
  agentEmail: string | null;
  marketCentre: string | null;
  agentMatched: boolean | null;
  listingMatched: boolean | null;
  agentMatchState: string;
  listingMatchState: string;
  contactMatchState: string;
  marketingStatus: 'CLEAR' | 'DO_NOT_CONTACT' | 'REVIEW_REQUIRED' | 'UNKNOWN';
  attention: string[];
  provisionalContactId: string | null;
  canonicalContactId: string | null;
};

export type ProvisionalEnquiry = UnifiedLead;

export type ProvisionalContact = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  owner: string | null;
  marketCentre: string | null;
  sources: LeadSource[];
  enquiryCount: number;
  lastActivity: string;
  leadIds: string[];
  provisional: true;
};

export type ApiUnifiedLead = {
  id: string;
  source: LeadSource;
  sourceLeadId: string | null;
  occurredAt: string | null;
  receivedAt: string;
  consumer: { name: string | null; email: string | null; phone: string | null };
  leadType: string | null;
  listing: {
    internalListingId: string | null;
    listingAgentKwuid: string | null;
    listingNumber: string | null;
    sourceReference: string | null;
    address: string | null;
    suburb: string | null;
    city: string | null;
    price: string | null;
    saleOrRent: string | null;
    url: string | null;
  };
  assignment: {
    associateId: string | null;
    associateKwuid?: string | null;
    agentName: string | null;
    agentEmail: string | null;
    teamId: string | null;
    teamName: string | null;
    marketCentreId: string | null;
    marketCentreName: string | null;
  };
  match: { agentMatched: boolean; listingMatched: boolean; agentMatchState: string; listingMatchState: string; contactMatchState: string };
  marketingStatus?: 'CLEAR' | 'DO_NOT_CONTACT' | 'REVIEW_REQUIRED' | 'UNKNOWN';
  message: string | null;
  lifecycleStatus: string;
  attention: string[];
  provisionalContactId?: string | null;
  canonicalContactId?: string | null;
  sourceMetadata: { agencyId: string | null; agencyName: string | null; sourceAccount: string | null };
};

export type ApiProvisionalContact = {
  provisional_id: string;
  name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  sources: LeadSource[];
  enquiry_count: number;
  first_seen_at: string;
  last_seen_at: string;
  agent_name: string | null;
  team_id: string | null;
  team_name: string | null;
  market_center_id: string | null;
  market_center_name: string | null;
};

export type ApiContactDetail = ApiProvisionalContact & {
  enquiries: ApiUnifiedLead[];
  associatedAgents: Array<{ name: string | null; kwuid?: string | null; marketCentre?: string | null; enquiryCount: number }>;
};

export function associateLabel(name: string | null | undefined, kwuid: string | null | undefined): string {
  const cleanName = name?.trim();
  const cleanKwuid = kwuid?.trim();
  if (cleanName && cleanKwuid) return `${cleanName} · KWUID ${cleanKwuid}`;
  if (cleanName) return cleanName;
  if (cleanKwuid) return `KWUID ${cleanKwuid}`;
  return 'Not attributed';
}

export function leadTypeLabel(value: string | null | undefined): string {
  const type = value?.trim();
  if (!type || /^\d+$/.test(type)) return 'Enquiry';
  if (type === 'WhatsAppLead') return 'WhatsApp Lead';
  return type.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function quickViewLabel(value: string): string {
  const labels: Record<string, string> = {
    all: 'All',
    today: 'Today',
    new: 'New',
    attention: 'Needs Attention',
    'unmatched-agent': 'Unmatched Agent',
    'unmatched-listing': 'Unmatched Listing',
    'my-leads': 'My Leads',
  };
  return labels[value] ?? value;
}

export function buildListingPath(listingId: string): string {
  return `/listings?review=${encodeURIComponent(listingId)}`;
}

export function buildPublicListingPath(listingAgentKwuid: string, listingNumber: string): string {
  return `https://kwhomes.co.za/${encodeURIComponent(listingAgentKwuid)}/${encodeURIComponent(listingNumber)}`;
}

export function isPublicKwHomesListing(listingAgentKwuid: string | null, listingNumber: string | null): boolean {
  return Boolean(listingAgentKwuid?.trim() && listingNumber && /^KWL\d+$/i.test(listingNumber.trim()));
}

export function buildContactPath(provisionalId: string, tab: string = 'contacts'): string {
  return `/leads-contacts?tab=${encodeURIComponent(tab)}&contact=${encodeURIComponent(provisionalId)}`;
}

export function calculateSourceShare(sourceCount: number, periodTotal: number): number | null {
  return periodTotal > 0 ? Math.round((sourceCount / periodTotal) * 1000) / 10 : null;
}

// A source absent from the period breakdown had zero leads in that period, not its all-time total.
export function periodSourceTotal(bySource: Array<{ source: string; total: number }>, source: string): number {
  return Number(bySource.find((item) => item.source === source)?.total ?? 0) || 0;
}

export function toLocalDateInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export type OverviewPeriodPreset = 'today' | '7d' | '30d' | '90d' | 'month';

export const overviewPeriodPresets: Array<{ id: OverviewPeriodPreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'month', label: 'This month' },
];

export function overviewPeriodRange(preset: OverviewPeriodPreset, now: Date = new Date()): { from: string; to: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === '7d') start.setDate(start.getDate() - 6);
  if (preset === '30d') start.setDate(start.getDate() - 29);
  if (preset === '90d') start.setDate(start.getDate() - 89);
  if (preset === 'month') start.setDate(1);
  return { from: toLocalDateInput(start), to: toLocalDateInput(now) };
}

export function matchOverviewPreset(from: string, to: string, now: Date = new Date()): OverviewPeriodPreset | null {
  return overviewPeriodPresets.find(({ id }) => {
    const range = overviewPeriodRange(id, now);
    return range.from === from && (range.to === to || (!to && id === 'month'));
  })?.id ?? null;
}

export function contactLinkState(lead: Pick<UnifiedLead, 'canonicalContactId' | 'contactMatchState'>): { label: string; tone: 'good' | 'warning' | 'neutral' } {
  if (lead.canonicalContactId || lead.contactMatchState === 'CANONICAL' || lead.contactMatchState === 'MATCHED') return { label: 'Matched', tone: 'good' };
  if (lead.contactMatchState === 'PROVISIONAL_CANDIDATE' || lead.contactMatchState === 'PROVISIONAL') return { label: 'Provisional', tone: 'warning' };
  if (lead.contactMatchState === 'REVIEW_REQUIRED') return { label: 'Matched · in review', tone: 'warning' };
  return { label: 'Unmatched', tone: 'neutral' };
}

export function clearLeadContactFilters(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  ['q', 'source', 'view', 'from', 'to', 'marketCentre', 'team', 'agent', 'leadType', 'saleOrRent', 'city', 'suburb', 'priceMin', 'priceMax', 'listingNumber', 'agentMatch', 'listingMatch', 'sort', 'direction', 'page', 'pageSize'].forEach((key) => next.delete(key));
  return next;
}

export type ApiOverview = {
  viewsSourceMix?: Record<LeadSource, number | null>;
  viewsTrend?: Array<{ date: string; property24: number | null; privateProperty: number | null; kwhomes: number | null }>;
  metrics: { totalLeads: number; leadsToday: number; uniqueProvisionalContacts: number; unmatchedAgents: number; unmatchedListings: number; missingContactDetails: number; needsAttentionLeads: number; attentionIssueCount: number };
  bySource: Array<{ source: LeadSource; total: number }>;
  trend: Array<{ date: string; source: LeadSource; total: number }>;
  sourceAvailability: Record<LeadSource, 'AVAILABLE' | 'UNAVAILABLE' | 'DISABLED' | 'UNKNOWN'>;
};

export function recordedViewsShare(total: number | null, mix: ApiOverview['viewsSourceMix']): number | null {
  if (total == null || !mix) return null;
  return calculateSourceShare(total, Object.values(mix).reduce<number>((sum, value) => sum + (value ?? 0), 0));
}

export function adaptApiLead(row: ApiUnifiedLead): UnifiedLead {
  return {
    id: row.id,
    source: row.source,
    sourceLabel: sourceLabels[row.source],
    sourceLeadId: row.sourceLeadId ?? row.id,
    leadType: row.leadType,
    occurredAt: row.occurredAt,
    receivedAt: row.receivedAt,
    consumerName: row.consumer.name,
    phone: row.consumer.phone,
    email: row.consumer.email,
    message: row.message,
    listingReference: row.listing.listingNumber,
    listingInternalId: row.listing.internalListingId,
    listingAgentKwuid: row.listing.listingAgentKwuid,
    listingExternalReference: row.listing.sourceReference,
    listingAddress: row.listing.address,
    suburb: row.listing.suburb,
    listingType: row.listing.saleOrRent,
    listingUrl: row.listing.url,
    price: row.listing.price,
    priceType: null,
    agentId: row.assignment.associateId,
    agentKwuid: row.assignment.associateKwuid ?? null,
    agentName: row.assignment.agentName,
    agentEmail: row.assignment.agentEmail,
    marketCentre: row.assignment.marketCentreName,
    agentMatched: row.match.agentMatched,
    listingMatched: row.match.listingMatched,
    agentMatchState: row.match.agentMatchState,
    listingMatchState: row.match.listingMatchState,
    contactMatchState: row.match.contactMatchState,
    marketingStatus: row.marketingStatus ?? 'UNKNOWN',
    attention: row.attention,
    provisionalContactId: row.provisionalContactId ?? null,
    canonicalContactId: row.canonicalContactId ?? null,
  };
}

export function adaptApiContact(row: ApiProvisionalContact): ProvisionalContact {
  return {
    id: `candidate:${row.provisional_id}`,
    name: row.name ?? 'Unknown consumer',
    email: row.primary_email,
    phone: row.primary_phone,
    owner: row.agent_name,
    marketCentre: row.market_center_name,
    sources: row.sources,
    enquiryCount: row.enquiry_count,
    lastActivity: row.last_seen_at,
    leadIds: [],
    provisional: true,
  };
}

const sourceLabels: Record<LeadSource, string> = {
  PROPERTY24: 'Property24',
  PRIVATE_PROPERTY: 'Private Property',
  KWHOMES: 'KWHomes',
};

export function normalizeEmail(value: string | null): string | null {
  const normalized = (value ?? '').trim().toLowerCase();
  return normalized || null;
}

export function normalizePhone(value: string | null): string | null {
  const compact = (value ?? '').trim().replace(/[\s().-]/g, '');
  if (!compact) return null;
  if (/^\+\d{7,15}$/.test(compact)) return compact;
  if (/^00\d{7,15}$/.test(compact)) return `+${compact.slice(2)}`;
  if (/^27[1-8]\d{8}$/.test(compact)) return `+${compact}`;
  if (/^0[1-8]\d{8}$/.test(compact)) return `+27${compact.slice(1)}`;
  if (/^\d{7,15}$/.test(compact)) return compact;
  return null;
}

function buildAttention(input: {
  agentMatched: boolean | null;
  listingMatched: boolean | null;
  email: string | null;
  phone: string | null;
}): string[] {
  const attention: string[] = [];
  if (input.agentMatched === false) attention.push('Unmatched agent');
  if (input.listingMatched === false) attention.push('Unmatched listing');
  if (!input.email && !input.phone) attention.push('Missing contact details');
  return attention;
}

export function adaptPortalLead(source: LeadSource, row: PortalLeadRow): UnifiedLead {
  const agentMatched = row.agent_email ? true : row.agent_id || row.agent_name ? null : false;
  const listingMatched = source === 'PROPERTY24'
    ? Boolean(row.listing_address || row.listing_type)
    : null;
  return {
    id: `${source}:${row.id}`,
    source,
    sourceLabel: source === 'PRIVATE_PROPERTY' ? 'Private Property' : 'Property24',
    sourceLeadId: row.lead_id,
    leadType: row.lead_type,
    occurredAt: row.lead_date_time,
    receivedAt: row.received_at,
    consumerName: row.lead_name,
    phone: row.lead_phone_number,
    email: row.lead_email,
    message: row.lead_message,
    listingReference: row.listing_reference,
    listingInternalId: null,
    listingAgentKwuid: null,
    listingExternalReference: row.listing_external_reference,
    listingAddress: row.listing_address,
    suburb: row.listing_suburb,
    listingType: row.listing_type,
    listingUrl: row.listing_url,
    price: row.price,
    priceType: row.price_type,
    agentId: row.agent_id,
    agentKwuid: null,
    agentName: row.agent_name,
    agentEmail: row.agent_email,
    marketCentre: row.market_center_name ?? row.agency_name,
    agentMatched,
    listingMatched,
    agentMatchState: agentMatched === true ? 'MATCHED' : agentMatched === false ? 'UNMATCHED' : 'NOT_SUPPLIED',
    listingMatchState: listingMatched === true ? 'MATCHED' : listingMatched === false ? 'UNMATCHED' : 'NOT_SUPPLIED',
    contactMatchState: row.lead_email && row.lead_phone_number ? 'PROVISIONAL_CANDIDATE' : 'UNMATCHED',
    marketingStatus: 'UNKNOWN',
    attention: buildAttention({ agentMatched, listingMatched, email: row.lead_email, phone: row.lead_phone_number }),
    provisionalContactId: null,
    canonicalContactId: null,
  };
}

export function buildProvisionalContacts(leads: UnifiedLead[]): ProvisionalContact[] {
  const contacts: ProvisionalContact[] = [];
  const exactKeys = new Map<string, ProvisionalContact>();

  for (const lead of leads) {
    const email = normalizeEmail(lead.email);
    const phone = normalizePhone(lead.phone);
    const key = email && phone ? `${email}|${phone}` : null;
    const existing = key ? exactKeys.get(key) : undefined;
    if (existing) {
      existing.enquiryCount += 1;
      existing.lastActivity = lead.occurredAt ?? lead.receivedAt;
      existing.leadIds.push(lead.id);
      if (!existing.sources.includes(lead.source)) existing.sources.push(lead.source);
      continue;
    }

    const contact: ProvisionalContact = {
      id: `candidate:${lead.id}`,
      name: lead.consumerName ?? 'Unknown consumer',
      email: lead.email,
      phone: lead.phone,
      owner: lead.agentName,
      marketCentre: lead.marketCentre,
      sources: [lead.source],
      enquiryCount: 1,
      lastActivity: lead.occurredAt ?? lead.receivedAt,
      leadIds: [lead.id],
      provisional: true,
    };
    contacts.push(contact);
    if (key) exactKeys.set(key, contact);
  }

  return contacts;
}

export function attentionLabel(value: string): string {
  const labels: Record<string, string> = {
    UNMATCHED_AGENT: 'Unmatched Agent',
    UNMATCHED_LISTING: 'Unmatched Listing',
    MISSING_CONTACT_DETAILS: 'Missing Contact Details',
    'Unmatched agent': 'Unmatched Agent',
    'Unmatched listing': 'Unmatched Listing',
    'Missing contact details': 'Missing Contact Details',
  };
  return labels[value] ?? value;
}